import { Field } from 'formik'
import type { FocusEvent } from 'react'
import React from 'react'

import {
  Banner,
  ButtonClean,
  ButtonGroup,
  CurrencyInput,
  Loading,
  Subsection,
  useMediaQueryContext,
} from '@fourthwall/components'
import { PRODUCT_PRICE_MAX } from '@fourthwall/utils/dist/constants'

import { CompareAtPriceCheckboxField } from '../components/CompareAtPriceCheckboxField'
import { ExplicitSellingPrice } from './components/ExplicitSellingPrice'
import { ImplicitSellingPrice } from './components/ImplicitSellingPrice'
import * as S from './styled'
import type { ProductPricingViewProps } from './types'

export const ProductPricingView = ({
  allowExplicitType,
  onlyExplicitTypeAvailable,
  implicitSellingPriceError,
  isAdmin,
  isLoading,
  profit,
  pricingType,
  sizePriceSuggestion,
  data,
  tableHeaders,
  costsLink,
  showMissingCostsBanner,
  disabled,
  isCompareAtPriceEnabled,
  isCreatorDeclaredCostEnabled,
  applyToAllVariants,
  onToggleView,
  onLearnMoreClick,
  onPriceBlur,
  onIsCompareAtPriceEnabledValueChange,
  onCompareAtPriceBlur,
}: ProductPricingViewProps) => {
  const media = useMediaQueryContext()
  const isExplicit = pricingType === 'EXPLICIT'

  // TODO: until we don't have implemented modal "HowToCalculateProfit" for PER_SIZE products
  // we should hide this button for them
  const showHowToCalculateProfitButton = isExplicit

  const renderSellingPriceContent = () => {
    if (showMissingCostsBanner) {
      const adminButton = <ButtonClean label="Set it up" to={costsLink} />

      return (
        <Banner appearance="warning">
          Product is missing costs. {isAdmin ? adminButton : 'Contact your administrator.'}
        </Banner>
      )
    }

    if (isExplicit) {
      return (
        <ExplicitSellingPrice
          data={data}
          tableHeaders={tableHeaders}
          applyToAllVariants={applyToAllVariants}
          isCompareAtPriceEnabled={isCompareAtPriceEnabled}
          disabled={disabled}
        />
      )
    }

    return (
      <ImplicitSellingPrice
        allowExplicitType={allowExplicitType}
        fieldName="sellingPriceValue"
        error={implicitSellingPriceError}
        sizePriceSuggestion={sizePriceSuggestion}
        profit={profit}
        disabled={disabled}
        onCustomizeClick={onToggleView}
        onLearnMoreClick={onLearnMoreClick}
        onBlur={(e) => onPriceBlur(e.target.value)}
      />
    )
  }

  const renderTypeSwitchButton = () => {
    if (!allowExplicitType || disabled) return null

    if (pricingType === 'IMPLICIT') return null

    return (
      <S.ButtonContainer small={!isExplicit}>
        <ButtonGroup space="large" direction={media.tablet ? 'row' : 'column'}>
          {onlyExplicitTypeAvailable ? null : (
            <ButtonClean
              appearance={isExplicit ? 'primary' : 'secondary'}
              label="Switch back to simple product pricing"
              onClick={onToggleView}
            />
          )}
          {showHowToCalculateProfitButton && !disabled && (
            <ButtonClean label="How we calculate profits" onClick={onLearnMoreClick} />
          )}
        </ButtonGroup>
      </S.ButtonContainer>
    )
  }

  const renderCompareAtPriceInput = () => {
    if (!isCompareAtPriceEnabled || isExplicit) return null

    return (
      <S.CompareAtPriceInputContainer>
        <Field
          component={CurrencyInput}
          name="compareAtPriceValue"
          label="Compare-at price"
          strikethrough
          fullWidth
          min={0}
          disabled={disabled}
          max={PRODUCT_PRICE_MAX}
          onBlur={(e: FocusEvent<HTMLInputElement>) => onCompareAtPriceBlur(e.target.value)}
        />
      </S.CompareAtPriceInputContainer>
    )
  }

  const renderCreatorDeclaredCostInput = () => {
    if (!isCreatorDeclaredCostEnabled || isExplicit) return null

    return (
      <S.CompareAtPriceInputContainer>
        <Field
          component={CurrencyInput}
          name="variants[0].creatorDeclaredCost.value"
          label="Product cost"
          fullWidth
          min={0}
          disabled={disabled}
        />
      </S.CompareAtPriceInputContainer>
    )
  }

  return (
    <Subsection title="Selling price" borderless={isExplicit}>
      <Loading isLoading={isLoading}>
        {renderSellingPriceContent()}
        {renderTypeSwitchButton()}
        {renderCreatorDeclaredCostInput()}
        <div style={{ marginTop: 12 }}>
          <CompareAtPriceCheckboxField
            disabled={disabled}
            onValueChange={onIsCompareAtPriceEnabledValueChange}
          />
        </div>
        {renderCompareAtPriceInput()}
      </Loading>
    </Subsection>
  )
}
